"use client";

import type { ComponentType } from "react";
import { toast as sonnerToast } from "sonner";
import { CheckCircle2, XCircle, Info, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

type ToastType = "success" | "error" | "info" | "promise";
type ToastStatus = "success" | "error" | "info" | "loading";

interface ToastOptions {
  title: string;
  description?: string;
  type: ToastType;
  promise?: Promise<unknown>;
  loading?: string;
  success?: string;
  error?: string;
}

/**
 * Stamp per status. The class strings are written out in full so Tailwind can
 * see them. The stamp's ink is `currentColor`, so its border, inner rule and
 * glyph all take the tone from the one `text-*` class.
 */
const STAMPS: Record<
  ToastStatus,
  {
    label: string;
    tone: string;
    icon: ComponentType<{ className?: string }>;
    role: "status" | "alert";
    duration: number;
  }
> = {
  success: {
    label: "Выполнено",
    tone: "text-success",
    icon: CheckCircle2,
    role: "status",
    duration: 4000,
  },
  error: {
    label: "Отказано",
    tone: "text-destructive",
    icon: XCircle,
    role: "alert",
    duration: 6000,
  },
  info: {
    label: "К сведению",
    tone: "text-info",
    icon: Info,
    role: "status",
    duration: 4000,
  },
  loading: {
    label: "В работе",
    tone: "text-muted-foreground",
    icon: Loader2,
    role: "status",
    duration: Infinity,
  },
};

interface CirculationToastProps {
  id: string | number;
  status: ToastStatus;
  title: string;
  description?: string;
  /** Fixed at creation so the date does not change when the card re-renders. */
  date: string;
}

/**
 * A library circulation card: header with the form name and date, a double
 * rule, ruled lines carrying the message, and the status inked as a rubber
 * stamp over the lower right corner. Rendered through `sonnerToast.custom`, so
 * sonner contributes only positioning and stacking, not its own chrome.
 */
function CirculationToast({
  id,
  status,
  title,
  description,
  date,
}: Readonly<CirculationToastProps>) {
  const { label, tone, icon: StampIcon, role } = STAMPS[status];

  return (
    <div
      role={role}
      className={cn(
        "relative w-full overflow-hidden rounded-xs border border-foreground/30",
        "bg-card font-serif text-card-foreground shadow-md",
        "outline-1 -outline-offset-4 outline-foreground/15",
      )}
    >
      {/* Header: form name, date, dismiss */}
      <div className="flex items-center justify-between gap-3 px-5 pt-3 pb-1.5 text-xs leading-none tracking-widest text-muted-foreground uppercase">
        <span>{title}</span>
        <span className="flex items-center gap-2">
          <Button
            type="button"
            onClick={() => sonnerToast.dismiss(id)}
            aria-label="Закрыть"
            variant="bare"
            size="icon-2xs"
            className="-mr-1"
          >
            <X className="size-3" aria-hidden="true" />
          </Button>
        </span>
      </div>

      <div className="mx-5 border-t-4 border-double border-input" />

      {/* Body: the message sits on the ruled lines; the stamp lands on top */}
      <div className="relative min-h-20 px-5 pt-1 pb-3">
        <div className="pr-28 leading-6 ruled-lines">
          {description && (
            <p className="text-sm text-muted-foreground italic wrap-break-word">
              {description}
            </p>
          )}
        </div>

        {/* Keyed by status so a loading card re-stamps when it settles. */}
        <div
          key={status}
          aria-hidden="true"
          className={cn(
            "absolute right-4 bottom-3 -rotate-6 select-none",
            "flex flex-col items-center gap-0.5 rounded-xs border-2 border-current px-3 py-1.5",
            "outline-1 -outline-offset-5 outline-current",
            "font-mono text-xs leading-none font-bold tracking-widest uppercase",
            "opacity-90 mix-blend-multiply dark:mix-blend-screen",
            status !== "loading" &&
              "animate-in fade-in zoom-in-150 duration-300 motion-reduce:animate-none",
            tone,
          )}
        >
          <span className="flex items-center gap-1">
            <StampIcon
              className={cn("size-3.5", status === "loading" && "animate-spin")}
            />
            {label}
          </span>
          <span className="text-xs font-medium tabular-nums opacity-80">
            {date}
          </span>
        </div>
      </div>
    </div>
  );
}

const today = () => new Date().toLocaleDateString("ru-RU");

function show(
  status: ToastStatus,
  title: string,
  description: string | undefined,
  date: string,
  id?: string | number,
) {
  return sonnerToast.custom(
    (toastId) => (
      <CirculationToast
        id={toastId}
        status={status}
        title={title}
        description={description}
        date={date}
      />
    ),
    { id, duration: STAMPS[status].duration },
  );
}

/**
 * Returns the toast id for every type. For `promise`, a single card is created
 * in the "loading" state and restamped in place when the promise settles.
 */
export function showToast({
  title,
  description,
  type,
  promise,
  loading,
  success,
  error,
}: ToastOptions) {
  const date = today();

  switch (type) {
    case "success":
      return show("success", title, description, date);
    case "error":
      return show("error", title, description, date);
    case "info":
      return show("info", title, description, date);
    case "promise": {
      if (!promise) {
        console.error("Promise type requires a promise to be provided");
        return;
      }
      const id = show("loading", loading || title, description, date);
      promise.then(
        () => show("success", success || "Success", description, date, id),
        () => show("error", error || "Error", description, date, id),
      );
      return id;
    }
    default:
      return show("info", title, description, date);
  }
}
